import React from "react"
import { Link } from "gatsby"
import Layout from "./Layout"

const siteUrl = "https://gregwoodwardphoto.com"

// Matches JOURNAL_ENABLED in gatsby-node.js. While the journal is off these
// links would point at pages that aren't built, so they're hidden. The
// relatedJournal data below is left intact, ready for switching back on.
const JOURNAL_ENABLED = false

const services = {
  commercial: {
    label: "01 / Commercial",
    title: "Commercial photography and video for brands with something to prove.",
    seoTitle: "Commercial Photographer London | Greg Woodward",
    description:
      "Greg Woodward is a commercial photographer and director working with brands and agencies across London and the UK, producing stills, video and social content from a single production: campaign imagery, product and lifestyle photography, corporate content and brand films, built around real people in real environments rather than a studio backdrop.",
    seoDescription:
      "London commercial photographer and director for brands and agencies. Campaign photography, brand film and social content from a single production.",
    questions: [
      [
        "What does a commercial production with Greg typically include?",
        "Most commercial briefs combine photography with supporting video: a hero campaign image, social cutdowns, behind-the-scenes footage and a short interview-led case-study film can all come from the same day on set, rather than three separate bookings.",
      ],
      [
        "Who is commercial photography and video for?",
        "Marketing, brand and creative teams at companies and agencies who need dependable, high-quality content for campaigns, websites, social and internal use, on an ongoing basis rather than once a year.",
      ],
      [
        "How much does commercial photography cost in the UK?",
        "Day rates typically range from £800 to £3,000+ depending on scope, usage rights and deliverables. Combining photography and video on the same production usually costs 20-35% less than booking them separately.",
      ],
      [
        "Where does Greg work?",
        "Greg is based in London and works across the UK and internationally, with experience shooting in over 40 countries across four continents.",
      ],
      [
        "Can photography and video really be produced on the same shoot?",
        "Yes. It's usually the most efficient way to work: one crew, one day, one visual language, and a wider library of usable content at the end of it.",
      ],
    ],
    relatedJournal: [
      { text: "What does commercial photography cost in the UK?", slug: "commercial-photography-cost-uk" },
      { text: "How to brief a commercial photographer", slug: "how-to-brief-a-commercial-photographer" },
      { text: "Combining photography and video on one production day", slug: "combining-photography-video-one-production-day" },
    ],
  },
  lifestyle: {
    label: "02 / Lifestyle & Travel",
    title: "Lifestyle and travel photography and film that feels lived in.",
    seoTitle: "Lifestyle & Travel Photographer | Greg Woodward",
    description:
      "Lifestyle and travel photography and video by Greg Woodward for tourism boards, hotel groups and travel brands: imagery and film that bring warmth, movement and a genuine sense of place to a campaign, rather than staged, stock-style perfection.",
    seoDescription:
      "Lifestyle and travel photographer for tourism, hospitality and travel brands. Location shoots across over 40 countries, based in London.",
    questions: [
      [
        "What makes Greg's lifestyle and travel work different?",
        "The work is directed with enough structure to serve a brief while leaving room for genuine interaction and imperfect, human detail. That's the difference between a campaign that feels real and one that feels staged.",
      ],
      [
        "Does lifestyle and travel work include video?",
        "Yes. Travel films, destination content and social edits are developed alongside stills so a campaign feels like one coherent visual world, not two separate productions with two different moods.",
      ],
      [
        "How much does a travel photography shoot cost?",
        "Travel shoots typically run £1,500-£3,500 per day plus travel costs. International shoots involve flights, accommodation and sometimes location scouting, which are quoted separately.",
      ],
      [
        "Where has Greg shot lifestyle and travel work?",
        "Across over 40 countries on four continents, solo and with full crews, for clients including TUI, Melia Hotels and Grosvenor Hotels.",
      ],
    ],
    relatedJournal: [
      { text: "Hotel photography that drives bookings", slug: "hotel-photography-drives-bookings" },
      { text: "Directed vs candid: getting authentic lifestyle imagery", slug: "directed-vs-candid-authentic-lifestyle-imagery" },
      { text: "Planning a location shoot abroad", slug: "planning-location-shoot-abroad" },
    ],
  },
  sports: {
    label: "03 / Sport & Documentary",
    title: "Sport and documentary photography and film built on access.",
    seoTitle: "Sports & Documentary Photographer | Greg Woodward",
    description:
      "Greg Woodward photographs and films athletes, teams and real-world stories, from campaign portraits for Team GB to documentary-style work for organisations such as Alzheimer's Research UK, with a focus on character, physicality and the atmosphere around performance and purpose.",
    seoDescription:
      "Sports and documentary photographer and filmmaker for athletes, teams and charities across the UK. Clients include Team GB and Alzheimer's Research UK.",
    questions: [
      [
        "What kind of sport and documentary work does Greg cover?",
        "Campaign portraits, reportage-led stories, training environments and social content, plus documentary photography and film for organisations telling real, public-interest stories.",
      ],
      [
        "Can Greg work on location, at speed, in unpredictable environments?",
        "Yes. Most of this work happens in real training environments, at live events or in the field, not under controlled studio conditions, so adapting to a changing schedule or environment is part of the job.",
      ],
      [
        "How do you commission photography for a charity campaign?",
        "Start with the story you want to tell, not a shot list. Charity photography works best when the photographer has time with the subjects and freedom to capture genuine moments rather than staged setups.",
      ],
      [
        "Does this include film as well as stills?",
        "Yes. Documentary-style video and interview-led films sit naturally alongside sport and documentary stills work, often from the same production.",
      ],
    ],
    relatedJournal: [
      { text: "How documentary photography supports charity campaigns", slug: "documentary-photography-charity-campaigns" },
      { text: "Sport photography for brands: beyond the action shot", slug: "sport-photography-brands-beyond-action" },
    ],
  },
  "moving-image": {
    label: "04 / Video & Film",
    title: "Video and film with a stills photographer's eye for a moment.",
    seoTitle: "Video Production & Brand Films London | Greg Woodward",
    description:
      "Greg directs and shoots video for brands and organisations that need film, photography and social content to share one visual language: from short documentary films and interview-led case studies to campaign films and behind-the-scenes content.",
    seoDescription:
      "Video production and brand film direction by Greg Woodward. Documentary films, case study videos and campaign content for organisations across the UK.",
    questions: [
      [
        "What kind of films does Greg make?",
        "Campaign films, branded content, social edits and short-form stories built around people, place and movement, with a particular focus on 3-5 minute interview-led case-study films that let a client, customer or team member tell the story in their own words.",
      ],
      [
        "How much does a brand film cost?",
        "A one-day shoot producing a 2-3 minute film typically costs £1,800-£3,500 depending on deliverables. Combining photography and film on the same day brings the per-deliverable cost down significantly.",
      ],
      [
        "Does Greg direct as well as shoot?",
        "Yes. Greg can lead the visual direction and camera work, collaborating with producers, crews and post-production teams as needed, or work solo for smaller productions.",
      ],
      [
        "Can video be produced alongside stills, on the same production?",
        "Yes, and it usually should be. One production can generate campaign photography, a case-study film and social cutdowns together, rather than commissioning each separately.",
      ],
    ],
    relatedJournal: [
      { text: "What is a case study film and why does it work?", slug: "case-study-film-why-it-works" },
      { text: "How to plan a brand film on a realistic budget", slug: "how-to-plan-brand-film-realistic-budget" },
      { text: "Why interview-led films work for B2B brands", slug: "interview-led-films-b2b-brands" },
    ],
  },
}

export default function ServicePage({ type, pageContext }) {
  type = type || pageContext?.type
  const service = services[type]
  const pathname = `/services/${type}/`

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        serviceType: service.title,
        name: service.title,
        description: service.seoDescription,
        provider: { "@type": "Person", name: "Greg Woodward", url: siteUrl },
        areaServed: ["London", "United Kingdom"],
        url: `${siteUrl}${pathname}`,
      },
      {
        "@type": "FAQPage",
        mainEntity: service.questions.map(([question, answer]) => ({
          "@type": "Question",
          name: question,
          acceptedAnswer: { "@type": "Answer", text: answer },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Services", item: `${siteUrl}/services/` },
          { "@type": "ListItem", position: 2, name: service.label.split(" / ")[1], item: `${siteUrl}${pathname}` },
        ],
      },
    ],
  }

  return (
    <Layout
      seo={{
        title: service.seoTitle || `${service.title} | Greg Woodward`,
        description: service.seoDescription,
        pathname,
        schema,
      }}
    >
      <main className="service-page">
        <div className="page-intro">
          <div>
            <span className="service-label">{service.label}</span>
            <h1>{service.title}</h1>
          </div>
          <p>{service.description}</p>
        </div>

        <div className="service-questions">
          <span className="service-label">A few useful answers</span>
          {service.questions.map(([question, answer]) => (
            <section key={question}>
              <h2>{question}</h2>
              <p>{answer}</p>
            </section>
          ))}
        </div>

        {JOURNAL_ENABLED && service.relatedJournal?.length > 0 && (
          <div className="service-journal">
            <span className="service-label">From the journal</span>
            <ul>
              {service.relatedJournal.map(({ text, slug }) => (
                <li key={slug}>
                  <Link to={`/journal/${slug}/`}>
                    {text} <span aria-hidden="true">↗</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="service-cta">
          <span>Have a brief in mind?</span>
          <Link to="/contact/">
            Start a conversation <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </main>
    </Layout>
  )
}
